import { Helmet } from 'react-helmet-async';
import { toSeoAbsoluteUrl, toSeoCanonicalUrl } from '../../utils/seoSiteUrl';

interface PageMetadataProps {
  title: string;
  description: string;
  /** Comma-separated keywords for this page. Improves extension/tool visibility. */
  keywords?: string;
  /** Canonical URL or route path for this page — prevents duplicate content penalties. */
  canonicalUrl?: string;
  /**
   * Robots directive for this page.
   * Defaults to 'index, follow' for all public pages.
   * Pass 'noindex, nofollow' for pages that should not be crawled.
   */
  robots?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterCard?: 'summary' | 'summary_large_image';
}

/**
 * PageMetadata component manages document head metadata using react-helmet-async.
 * Provides consistent title formatting, meta descriptions, Open Graph tags,
 * Twitter Card tags, canonical URL link element, and robots directive.
 */
export default function PageMetadata({
  title,
  description,
  keywords,
  canonicalUrl,
  robots = 'index, follow',
  ogTitle,
  ogDescription,
  ogImage,
  twitterCard = 'summary_large_image',
}: PageMetadataProps) {
  // Use provided OG values or fall back to main title/description
  const finalOgTitle = ogTitle || title;
  const finalOgDescription = ogDescription || description;

  const fallbackCanonicalPath = typeof window !== 'undefined'
    ? `${window.location.pathname}${window.location.search}${window.location.hash}` || '/'
    : '/';

  const resolvedCanonicalUrl = toSeoCanonicalUrl(canonicalUrl || fallbackCanonicalPath);

  // Default brand image for pages without specific images
  const finalOgImage = toSeoAbsoluteUrl(ogImage || '/logo-side.png');

  // Stable tags — always rendered so Helmet never adds/removes children conditionally,
  // which avoids the react-helmet-async removeChild reconciliation bug.
  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords || ''} />
        <meta name="publisher" content="AttendIn" />
        <meta name="robots" content={robots} />

        {/* Open Graph — og:url falls back to empty string when no canonical is set */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="AttendIn" />
        <meta property="og:title" content={finalOgTitle} />
        <meta property="og:description" content={finalOgDescription} />
        <meta property="og:image" content={finalOgImage} />
        <meta property="og:url" content={resolvedCanonicalUrl || ''} />

        {/* Twitter Card */}
        <meta name="twitter:card" content={twitterCard} />
        <meta name="twitter:title" content={finalOgTitle} />
        <meta name="twitter:description" content={finalOgDescription} />
        <meta name="twitter:image" content={finalOgImage} />
      </Helmet>

      {/* Canonical is always rendered exactly once for indexable reliability. */}
      <Helmet>
        <link rel="canonical" href={resolvedCanonicalUrl} />
      </Helmet>
    </>
  );
}
